<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { computed, shallowRef } from "vue"

import BatchGroupCard from "@/components/batches/BatchGroupCard.vue"
import BatchGroupReview from "@/components/batches/BatchGroupReview.vue"
import ContainerPicker from "@/components/ContainerPicker.vue"
import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import { useBatchMutations } from "@/composables/useBatchMutations"
import { useNavbar } from "@/composables/useNavbar"
import { formatDate } from "@/lib/format"
import { ICON } from "@/lib/icons"
import { batchesPath } from "@/routes"
import type { CaptureBatch, CaptureGroup } from "@/types/capture"
import type { ContainerSummary } from "@/types/models"

interface Option {
  id: string
  name: string
}

interface ContainerOption {
  label: string
  value: string
}

const props = defineProps<{
  batch: CaptureBatch
  item_types: Option[]
  containers: ContainerOption[]
  recent_containers: ContainerSummary[]
  all_containers: ContainerSummary[]
}>()

const currentBatch = shallowRef(props.batch)
const reviewingGroupId = shallowRef<string | null>(null)
const finishModalOpen = shallowRef(false)
const archiveModalOpen = shallowRef(false)
const deleteModalOpen = shallowRef(false)
const containerPickerOpen = shallowRef(false)
const containerEditable = computed(
  () => currentBatch.value.status === "capturing" || currentBatch.value.status === "ready",
)
const resolvedCount = computed(
  () =>
    currentBatch.value.groups.filter(
      (group) => group.status === "completed" || group.status === "skipped",
    ).length,
)

const { title } = useNavbar({ title: currentBatch.value.name })
const mutations = useBatchMutations({ replaceGroup, replaceGroups, replaceBatch })

function toggleReview(groupId: string) {
  reviewingGroupId.value = reviewingGroupId.value === groupId ? null : groupId
}

function replaceGroup(group: CaptureGroup) {
  const nextGroups = currentBatch.value.groups.map((candidate) =>
    candidate.id === group.id ? group : candidate,
  )
  currentBatch.value = batchWithGroups(nextGroups)
  if (group.status === "completed" || group.status === "skipped") {
    reviewingGroupId.value = null
  }
}

function replaceGroups(groups: CaptureGroup[]) {
  const nextGroups = [...groups]
  nextGroups.sort(
    (left, right) => left.position - right.position || left.id.localeCompare(right.id),
  )
  currentBatch.value = batchWithGroups(nextGroups)
  if (reviewingGroupId.value && !nextGroups.some((group) => group.id === reviewingGroupId.value)) {
    reviewingGroupId.value = null
  }
}

function batchWithGroups(nextGroups: CaptureGroup[]): CaptureBatch {
  return {
    ...currentBatch.value,
    groups: nextGroups,
    group_count: nextGroups.length,
    photo_count: nextGroups.reduce((total, group) => total + group.actual_photo_count, 0),
    unresolved_group_count: nextGroups.filter(isUnresolved).length,
  }
}

function replaceBatch(batch: CaptureBatch) {
  currentBatch.value = batch
}

function isUnresolved(group: CaptureGroup) {
  return group.status !== "completed" && group.status !== "skipped"
}

function mergeCandidatesFor(groupId: string) {
  return currentBatch.value.groups.filter(
    (candidate) => candidate.id !== groupId && isUnresolved(candidate),
  )
}

async function deletePhoto(groupId: string, photoId: string, complete: (success: boolean) => void) {
  complete(Boolean(await mutations.deletePhoto(groupId, photoId)))
}

async function splitGroup(
  groupId: string,
  photoIds: string[],
  complete: (success: boolean) => void,
) {
  complete(Boolean(await mutations.splitGroup(groupId, photoIds)))
}

async function mergeGroups(
  groupId: string,
  sourceGroupIds: string[],
  complete: (success: boolean) => void,
) {
  complete(Boolean(await mutations.mergeGroups(groupId, sourceGroupIds)))
}

async function archiveBatch() {
  const result = await mutations.archive(currentBatch.value.id)
  if (result) archiveModalOpen.value = false
}

async function finishBatch() {
  const result = await mutations.finish(currentBatch.value.id)
  if (result) finishModalOpen.value = false
}

async function changeBatchContainer(container: ContainerSummary | null) {
  const result = await mutations.updateContainer(currentBatch.value.id, container?.id ?? null)
  if (result) containerPickerOpen.value = false
}

function openArchiveModal() {
  archiveModalOpen.value = true
}

function openFinishModal() {
  finishModalOpen.value = true
}

function openDeleteModal() {
  deleteModalOpen.value = true
}

function openContainerPicker() {
  containerPickerOpen.value = true
}

async function deleteBatch() {
  if (await mutations.deleteBatch(currentBatch.value.id)) {
    router.visit(batchesPath())
  }
}
</script>

<template>
  <Head :title="title" />

  <div class="space-y-5">
    <header class="space-y-3">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="text-highlighted text-xl font-semibold">{{ currentBatch.name }}</h1>
          <p class="text-muted text-sm">
            {{ formatDate(currentBatch.created_at) }} · {{ currentBatch.created_by.name }}
          </p>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <UBadge color="neutral" variant="subtle">{{ currentBatch.status }}</UBadge>
          <UButton
            v-if="currentBatch.status === 'capturing'"
            label="Finish capture"
            color="primary"
            variant="solid"
            :icon="ICON.check"
            data-testid="open-finish-batch"
            class="min-h-11"
            @click="openFinishModal"
          />
          <UButton
            v-if="currentBatch.status !== 'archived'"
            label="Archive"
            color="neutral"
            variant="outline"
            :icon="ICON.archive"
            data-testid="open-archive"
            class="min-h-11"
            @click="openArchiveModal"
          />
          <UButton
            label="Delete"
            color="error"
            variant="outline"
            :icon="ICON.delete"
            data-testid="open-delete-batch"
            class="min-h-11"
            @click="openDeleteModal"
          />
        </div>
      </div>

      <UButton
        v-if="containerEditable"
        :label="currentBatch.container?.name ?? 'Choose container'"
        color="neutral"
        variant="link"
        :icon="ICON.container"
        :loading="mutations.actionFor(currentBatch.id) === 'container'"
        data-testid="change-batch-container"
        class="min-h-11 px-0"
        @click="openContainerPicker"
      />
      <p v-else class="text-muted flex items-center gap-2 text-sm" data-testid="batch-container">
        <UIcon :name="ICON.container" class="size-4" />
        {{ currentBatch.container?.name ?? "No container" }}
      </p>

      <div class="space-y-1.5">
        <div class="flex justify-between gap-3 text-sm">
          <span class="text-highlighted font-medium">
            {{ resolvedCount }} of {{ currentBatch.groups.length }} groups reviewed
          </span>
          <span class="text-muted">{{ currentBatch.photo_count }} photos</span>
        </div>
        <div
          class="bg-elevated h-2 overflow-hidden rounded-full"
          role="progressbar"
          :aria-valuenow="resolvedCount"
          aria-valuemin="0"
          :aria-valuemax="currentBatch.groups.length"
        >
          <div
            class="bg-primary h-full rounded-full transition-all"
            :style="{
              width: currentBatch.groups.length
                ? `${(resolvedCount / currentBatch.groups.length) * 100}%`
                : '0%',
            }"
          />
        </div>
      </div>
    </header>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <BatchGroupCard
        v-for="group in currentBatch.groups"
        :key="group.id"
        :group="group"
        :reviewing="reviewingGroupId === group.id"
        :reviewable="currentBatch.status === 'ready' && isUnresolved(group)"
        @review="toggleReview"
      >
        <template #review>
          <BatchGroupReview
            :group="group"
            :item-types="item_types"
            :containers="containers"
            :default-container-id="currentBatch.container?.id ?? null"
            :pending-action="mutations.actionFor(group.id)"
            :error="mutations.errorFor(group.id)"
            :merge-candidates="mergeCandidatesFor(group.id)"
            @create="mutations.createItem(group.id, $event)"
            @attach="mutations.attach(group.id, $event)"
            @analyze="mutations.analyze(group.id)"
            @rotate="mutations.rotate(group.id, $event)"
            @skip="mutations.skip(group.id)"
            @delete-photo="(photoId, complete) => deletePhoto(group.id, photoId, complete)"
            @split-group="(photoIds, complete) => splitGroup(group.id, photoIds, complete)"
            @merge-groups="(groupIds, complete) => mergeGroups(group.id, groupIds, complete)"
          />
        </template>
      </BatchGroupCard>
    </div>

    <p v-if="mutations.errorFor(currentBatch.id)" class="text-error text-sm" role="alert">
      {{ mutations.errorFor(currentBatch.id) }}
    </p>
    <ContainerPicker
      v-model:open="containerPickerOpen"
      :recent-containers="recent_containers"
      :all-containers="all_containers"
      allow-clear
      @select="changeBatchContainer"
      @clear="changeBatchContainer(null)"
    />
    <DeleteConfirmModal
      v-model:open="finishModalOpen"
      title="Finish capturing this batch?"
      action-label="Finish capture"
      action-color="primary"
      :action-icon="ICON.check"
      :loading="mutations.actionFor(currentBatch.id) === 'finish'"
      @confirm="finishBatch"
    >
      <div class="space-y-4">
        <p>Capture will end and this batch will be ready to review.</p>
        <p v-if="mutations.errorFor(currentBatch.id)" class="text-error text-sm" role="alert">
          {{ mutations.errorFor(currentBatch.id) }}
        </p>
      </div>
    </DeleteConfirmModal>
    <DeleteConfirmModal
      v-model:open="archiveModalOpen"
      title="Archive this batch?"
      action-label="Archive batch"
      action-color="warning"
      :action-icon="ICON.archive"
      :loading="mutations.actionFor(currentBatch.id) === 'archive'"
      @confirm="archiveBatch"
    >
      <div class="space-y-4">
        <p>The batch will leave the Open list. Its photos and inventory items stay intact.</p>
        <p v-if="mutations.errorFor(currentBatch.id)" class="text-error text-sm" role="alert">
          {{ mutations.errorFor(currentBatch.id) }}
        </p>
      </div>
    </DeleteConfirmModal>
    <DeleteConfirmModal
      v-model:open="deleteModalOpen"
      title="Delete this batch permanently?"
      action-label="Delete batch"
      :action-icon="ICON.delete"
      :loading="mutations.actionFor(currentBatch.id) === 'delete'"
      @confirm="deleteBatch"
    >
      <div class="space-y-4">
        <p>
          This permanently deletes the batch, its review data, and its original uploads. Inventory
          items and their copied photos stay intact.
        </p>
        <p v-if="mutations.errorFor(currentBatch.id)" class="text-error text-sm" role="alert">
          {{ mutations.errorFor(currentBatch.id) }}
        </p>
      </div>
    </DeleteConfirmModal>
  </div>
</template>
